import {
  collection,
  doc,
  getDocs,
  limit,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  where
} from "firebase/firestore";
import { db } from "../firebase/client";
import type { UserProfile } from "../types/domain";
import { getTravelWorldSnapshot } from "../auth/profileService";
import { loadTravelerQuizProfile } from "../quiz/travelerQuizService";
import { listTrips } from "../trips/tripStore";
import { loadWorldReviewsByAuthor } from "./worldCommunityService";
import { travelCountryContinent } from "./worldCountries";
import {
  emptyGamificationBreakdown,
  gamificationBadges,
  gamificationLevel,
  gamificationScore,
  type GamificationBadgeProgress,
  type GamificationBreakdown,
  type GamificationLevelId
} from "./gamificationRules";

const COMMUNITY_COLLECTION = "travelerQuizCommunity";
const LOCAL_VERSION = 1;

export interface GamificationHistoryEntry {
  id: string;
  delta: number;
  score: number;
  reason: "initial" | "progress";
  createdAt: string;
}

export interface GamificationSummary {
  uid: string;
  username: string;
  usernameNormalized: string;
  displayName: string;
  photoUrl?: string;
  score: number;
  baseScore: number;
  votePoints: number;
  level: GamificationLevelId;
  badges: GamificationBadgeProgress[];
  breakdown: GamificationBreakdown;
  history: GamificationHistoryEntry[];
  updatedAt: string;
}

export interface GamificationLeaderboardEntry extends GamificationSummary {
  rank?: number;
}

function localKey(uid: string): string {
  return `tripai.gamification.v${LOCAL_VERSION}.${uid}`;
}

function normalizeUsername(value: string): string {
  return String(value || "").trim().toLocaleLowerCase();
}

function safeNumber(value: unknown): number {
  const parsed = Number(value || 0);
  return Number.isFinite(parsed) ? Math.max(0, Math.floor(parsed)) : 0;
}

function normalizeBreakdown(value: unknown): GamificationBreakdown {
  const raw = value && typeof value === "object" ? value as Partial<Record<keyof GamificationBreakdown, unknown>> : {};
  return {
    visitedCountries: safeNumber(raw.visitedCountries),
    quizAnswers: safeNumber(raw.quizAnswers),
    travelFeedbacks: safeNumber(raw.travelFeedbacks),
    completedTrips: safeNumber(raw.completedTrips),
    publicReviews: safeNumber(raw.publicReviews),
    helpfulVotes: safeNumber(raw.helpfulVotes),
    notHelpfulVotes: safeNumber(raw.notHelpfulVotes),
    asiaCountries: safeNumber(raw.asiaCountries)
  };
}

function normalizeGamification(uid: string, value: unknown): GamificationSummary | null {
  if (!value || typeof value !== "object") return null;
  const raw = value as Record<string, unknown>;
  const breakdown = normalizeBreakdown(raw.breakdown);
  const calculated = gamificationScore(breakdown);
  const score = Number.isFinite(Number(raw.score)) ? Math.max(0, Math.floor(Number(raw.score))) : calculated.score;
  const baseScore = Number.isFinite(Number(raw.baseScore)) ? Math.max(0, Math.floor(Number(raw.baseScore))) : calculated.baseScore;
  const votePoints = Number.isFinite(Number(raw.votePoints)) ? Math.floor(Number(raw.votePoints)) : calculated.votePoints;
  const username = String(raw.username || "").trim();
  const historyRaw = Array.isArray(raw.history) ? raw.history : [];
  const history: GamificationHistoryEntry[] = historyRaw.flatMap((item, index) => {
    if (!item || typeof item !== "object") return [];
    const entry = item as Record<string, unknown>;
    const scoreValue = Math.max(0, Math.floor(Number(entry.score || 0)));
    const deltaValue = Math.floor(Number(entry.delta || 0));
    return [{
      id: String(entry.id || `history_${index}`),
      delta: Number.isFinite(deltaValue) ? deltaValue : 0,
      score: Number.isFinite(scoreValue) ? scoreValue : 0,
      reason: String(entry.reason) === "initial" ? "initial" : "progress",
      createdAt: String(entry.createdAt || "")
    } as GamificationHistoryEntry];
  }).slice(-12);
  return {
    uid,
    username,
    usernameNormalized: String(raw.usernameNormalized || normalizeUsername(username)),
    displayName: String(raw.displayName || username || "Mundely").trim(),
    photoUrl: String(raw.photoUrl || "").trim() || undefined,
    score,
    baseScore,
    votePoints,
    level: gamificationLevel(score),
    badges: gamificationBadges(breakdown),
    breakdown,
    history,
    updatedAt: String(raw.updatedAt || "")
  };
}

function readLocal(uid: string): GamificationSummary | null {
  try {
    const raw = localStorage.getItem(localKey(uid));
    if (!raw) return null;
    return normalizeGamification(uid, JSON.parse(raw));
  } catch {
    return null;
  }
}

function writeLocal(summary: GamificationSummary): void {
  try { localStorage.setItem(localKey(summary.uid), JSON.stringify(summary)); } catch { /* cache only */ }
}

export async function refreshGamification(uid: string, profile: UserProfile): Promise<GamificationSummary> {
  const [quiz, reviews] = await Promise.all([
    loadTravelerQuizProfile(uid),
    loadWorldReviewsByAuthor(uid)
  ]);
  const world = getTravelWorldSnapshot(uid, profile.preferences.travelWorldSync || profile.travelWorld, profile.visitedCountries);
  const trips = listTrips(uid);
  const breakdown = emptyGamificationBreakdown();
  breakdown.visitedCountries = world.countries.length;
  breakdown.quizAnswers = quiz.answers.length;
  breakdown.travelFeedbacks = [
    ...world.countries.map((item) => item.privateNote || item.feedback || ""),
    ...world.cities.map((item) => item.privateNote || item.feedback || "")
  ].filter((value) => String(value).trim().length > 0).length;
  breakdown.completedTrips = trips.filter((trip) => trip.status === "completed" && !trip.archivedAt).length;
  const worldPublicReviews = [
    ...world.countries.map((item) => item.publicReview || ""),
    ...world.cities.map((item) => item.publicReview || "")
  ].filter((value) => String(value).trim().length > 0).length;
  breakdown.publicReviews = Math.max(reviews.length, worldPublicReviews);
  breakdown.helpfulVotes = reviews.reduce((sum, review) => sum + review.helpfulCount, 0);
  breakdown.notHelpfulVotes = reviews.reduce((sum, review) => sum + review.notHelpfulCount, 0);
  breakdown.asiaCountries = world.countries.filter((item) => travelCountryContinent(item.countryCode) === "asia").length;

  const points = gamificationScore(breakdown);
  const now = new Date().toISOString();
  const previous = readLocal(uid);
  const previousHistory = previous?.history || [];
  const delta = previous ? points.score - previous.score : points.score;
  const historyEntry: GamificationHistoryEntry = {
    id: `score_${Date.now()}`,
    delta,
    score: points.score,
    reason: previous ? "progress" : "initial",
    createdAt: now
  };
  const history: GamificationHistoryEntry[] = delta !== 0 || previousHistory.length === 0
    ? [...previousHistory, historyEntry].slice(-12)
    : previousHistory;
  const summary: GamificationSummary = {
    uid,
    username: profile.username,
    usernameNormalized: normalizeUsername(profile.username),
    displayName: `${profile.firstName || ""} ${profile.lastName || ""}`.trim() || profile.username,
    photoUrl: profile.photoUrl,
    score: points.score,
    baseScore: points.baseScore,
    votePoints: points.votePoints,
    level: gamificationLevel(points.score),
    badges: gamificationBadges(breakdown),
    breakdown,
    history,
    updatedAt: now
  };
  writeLocal(summary);

  // The community document already exists for the Traveler Quiz and is readable by the
  // community features. Gamification is namespaced so quiz choices remain independent.
  try {
    await setDoc(doc(db, COMMUNITY_COLLECTION, uid), {
      gamification: {
        uid: summary.uid,
        username: summary.username,
        usernameNormalized: summary.usernameNormalized,
        displayName: summary.displayName,
        photoUrl: summary.photoUrl || "",
        score: summary.score,
        baseScore: summary.baseScore,
        votePoints: summary.votePoints,
        breakdown: summary.breakdown,
        history: summary.history,
        updatedAt: summary.updatedAt,
        serverUpdatedAt: serverTimestamp()
      }
    }, { merge: true });
  } catch {
    // Personal score remains available locally even if the public ranking is temporarily unavailable.
  }
  return summary;
}

export function getCachedGamification(uid: string): GamificationSummary | null {
  return readLocal(uid);
}

export async function loadGamificationLeaderboard(maxEntries = 50): Promise<GamificationLeaderboardEntry[]> {
  try {
    const snapshot = await getDocs(query(
      collection(db, COMMUNITY_COLLECTION),
      orderBy("gamification.score", "desc"),
      limit(Math.max(1, Math.min(100, maxEntries)))
    ));
    return snapshot.docs.flatMap((entry, index) => {
      const data = entry.data() as Record<string, unknown>;
      const normalized = normalizeGamification(entry.id, data.gamification);
      if (!normalized || !normalized.username) return [];
      return [{ ...normalized, rank: index + 1 }];
    });
  } catch {
    return [];
  }
}

export async function loadPublicGamificationByUsername(username: string): Promise<GamificationSummary | null> {
  const normalizedUsername = normalizeUsername(username);
  if (!normalizedUsername) return null;
  try {
    const snapshot = await getDocs(query(
      collection(db, COMMUNITY_COLLECTION),
      where("gamification.usernameNormalized", "==", normalizedUsername),
      limit(1)
    ));
    const entry = snapshot.docs[0];
    if (!entry) return null;
    const data = entry.data() as Record<string, unknown>;
    return normalizeGamification(entry.id, data.gamification);
  } catch {
    return null;
  }
}
